import React from "react";
import Logo from "../components/Logo";
import BackButton from "../components/BackButton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import JoinWaitlistButton from "@/components/JoinWaitlistButton";

const HowItWorks = () => {
  return (
    <div className="min-h-[100dvh] flex flex-col items-center p-6 gradient-background">
      <BackButton />
      <div className="w-full max-w-4xl mx-auto">
        <div className="flex justify-center">
          <Logo />
        </div>
        
        <div className="mt-12 space-y-8 text-center">
          <h1 
            className="text-2xl md:text-4xl font-bold text-black animate-fade-up opacity-0"
            style={{ animationDelay: "0.2s" }}
          >
            How it Works
            <span className="block mt-2 text-xl md:text-2xl italic">Reshape Your Dining Experience</span>
          </h1>

          <div 
            className="mt-16 space-y-24 text-left animate-fade-up opacity-0"
            style={{ animationDelay: "0.4s" }}
          >
            <div>
              <h2 className="text-3xl font-bold text-black mb-8">Joining EatsBoost</h2>
              <div className="bg-gradient-to-br from-white to-[#33C3F0] rounded-xl p-8 space-y-12 shadow-lg">
                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 top-0 w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-eatsboost-orange">1</span>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-black mb-2">Buy a Boost Card</h2>
                    <p className="text-black text-lg">Get your <span className="font-bold">exclusive</span> Boost Card for just <span className="font-bold">$10</span> — a one-time purchase for <span className="font-bold">lifelong</span> offers on the food you love!</p>
                    <div className="mt-4 p-4 bg-white rounded-lg border border-white/20">
                      <p className="text-black text-sm">
                        <span className="font-bold">Note:</span> Each eatery has a limited number of Boost Cards. Once they're gone, they're gone!
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 top-0 w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-eatsboost-orange">2</span>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-black mb-2">Scan the QR Code in the App</h2>
                    <p className="text-black text-lg">Add the eatery by scanning the QR code on the back of your <span className="font-bold">Boost Card</span></p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-black mb-8">Turn Visits Into Exclusive Offers</h2>
              <div className="bg-gradient-to-br from-white to-[#FFC3A0] rounded-xl p-8 space-y-12 shadow-lg">
                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 top-0 w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-eatsboost-orange">3</span>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-black mb-2">Log Visits by Uploading Receipts</h2>
                    <p className="text-black text-lg">Make <span className="font-bold">one regular visit</span> (with <span className="font-bold">minimum spend</span>), upload your <span className="font-bold">receipt</span>, and generate offers for the next 30 days</p>
                    <div className="mt-4 p-4 bg-white rounded-lg border border-white/20">
                      <p className="text-black text-sm">
                        <span className="font-bold">Example</span>: Papa's Pizzeria - 1 regular visit & $9.99 spend per visit to unlock a <span className="font-bold">Boost Cycle</span>.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 top-0 w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-eatsboost-orange">4</span>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-black mb-2">Generate Offers</h2>
                    <p className="text-black text-lg">Once we approve your visit, you can generate <span className="font-bold">2 offers every 7 days</span> — redeem them before they expire!</p>
                    <div className="mt-4 p-4 bg-white rounded-lg border border-white/20">
                      <p className="text-black text-sm">
                        <span className="font-bold">Note:</span> Each offer is either <span className="font-bold">Level 1</span>, <span className="font-bold">Level 2</span>, or <span className="font-bold">Level 3</span>. The higher the level, the more <span className="font-bold">rare and valuable</span> the offer
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-black mb-8">Redeem & Repeat</h2>
              <div className="bg-gradient-to-br from-white to-[#A770EF] rounded-xl p-8 space-y-12 shadow-lg">
                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 top-0 w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-eatsboost-orange">5</span>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-black mb-2">Redeem</h2>
                    <p className="text-black text-lg">When ready to redeem, <b>activate</b> the offer in the app and <b>show the server</b></p>
                  </div>
                </div>

                <div className="relative pl-16 md:pl-20">
                  <div className="absolute left-0 top-0 w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-eatsboost-orange">6</span>
                  </div>
                  <div>
                    <h2 className="text-xl md:text-2xl font-semibold text-black mb-2">Repeat</h2>
                    <p className="text-black text-lg"><b>Keep the cycle going</b>: every time a Boost Cycle ends, just <b>log another visit</b> to <b>unlock 30 more days of offers</b>. <b>Visit, unlock, repeat!</b></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 flex justify-center">
          <JoinWaitlistButton 
            className="animate-fade-up opacity-0 bg-white text-black hover:bg-gray-100"
            style={{ animationDelay: "0.6s" }}
          >
            Get Started
          </JoinWaitlistButton>
        </div>

        <div className="mt-16 w-full">
          <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>

          <Accordion type="single" collapsible className="w-full bg-white/10 rounded-lg border border-white/20 divide-y divide-white/20">
            <AccordionItem value="item-0" className="px-4 border-none">
              <AccordionTrigger className="text-center justify-center">
                What Exactly Are Offers?
              </AccordionTrigger>
              <AccordionContent>
                Offers are <span className="font-bold">exclusive discounts</span> on the food you love at the places you love. You reserve the <span className="font-bold">privilege</span> of generating them by purchasing a Boost Card. These offers are <span className="font-bold">not</span> available for all - only those who have <span className="font-bold">joined EatsBoost</span> through the purchase of Boost Cards
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-10" className="px-4 border-none">
              <AccordionTrigger className="text-center justify-center">
                What Perks Do I Get When I Buy a Boost Card
              </AccordionTrigger>
              <AccordionContent>
                The moment you join, you'll instantly receive a <span className="font-bold">Level 3 Welcome Offer</span>—our rarest and most valuable offer. Each eatery has a <span className="font-bold">limited number</span> of Boost Cards available, ensuring <span className="font-bold">exclusivity</span> for cardholders. As long as you keep this eatery on your profile, you'll continue unlocking <span className="font-bold">personalized offers</span>, helping you save money on the food you love—<span className="font-bold">now and in the long term</span>.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-9" className="px-4 border-none">
              <AccordionTrigger className="text-center justify-center">
                Is it Possible to Have the Same Eatery Listed Multiple Times on My Profile?
              </AccordionTrigger>
              <AccordionContent>
                No, each eatery can only be added once at a time to your profile.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-1" className="px-4 border-none">
              <AccordionTrigger className="text-center justify-center">
                How Many Eateries Can I Have On My Profile?
              </AccordionTrigger>
              <AccordionContent>
                You can have up to <span className="font-bold">3 eateries</span> on your profile.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="px-4 border-none">
              <AccordionTrigger className="text-center justify-center">
                How Do I Redeem Offers?
              </AccordionTrigger>
              <AccordionContent>
                In the <span className="font-bold">EatsBoost app</span>, navigate to the "<span className="font-bold">My Offers</span>" page and tap "<span className="font-bold">Activate</span>" beside the offer. A confirmation screen will appear. Upon confirming, you will have <span className="font-bold">15 seconds</span> to show the server, and they will process the offer for you. Enjoy!
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="px-4 border-none">
              <AccordionTrigger className="text-center justify-center">
                How Many Offers Can I Redeem per Day?
              </AccordionTrigger>
              <AccordionContent>
                There is no limit on how many offers you can redeem per day. <span className="font-bold">However, please note</span> that offers <span className="font-bold">cannot</span> be combined - they can <span className="font-bold">only be used one at a time</span>.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="px-4 border-none">
              <AccordionTrigger className="text-center justify-center">
                Can I Remove Eateries From My Profile?
              </AccordionTrigger>
              <AccordionContent>
                Yes, you can remove an eatery from your profile. <span className="font-bold">However</span>, your <span className="font-bold">Boost Card</span> for that eatery will be deactivated, and if you wish to add it again, you'll need to purchase a new <span className="font-bold">Boost Card</span>. <span className="font-bold">Please note</span> that availability is not guaranteed—<span className="font-bold">Boost Cards may be sold out</span> by the time you try to re-add.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="px-4 border-none">
              <AccordionTrigger className="text-center justify-center">
                When Do Offers Expire?
              </AccordionTrigger>
              <AccordionContent>
                Offers expire <span className="font-bold">7 days</span> after they were generated
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-7" className="px-4 border-none">
              <AccordionTrigger className="text-center justify-center">
                How Long Can I Generate Offers For?
              </AccordionTrigger>
              <AccordionContent>
                There is <span className="font-bold">no limit</span> to how many offers you can generate over time. As long as you keep an eatery in your profile, you can continue entering <span className="font-bold">Boost Cycles</span> and generate <span className="font-bold">2 offers every 7 days</span> until the Boost Cycle ends. If you would like to start another Boost Cycle, you can do so right after your visits are <span className="font-bold">complete and approved</span>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-8" className="px-4 border-none">
              <AccordionTrigger className="text-center justify-center">
                Can I Use Someone Else's Boost Card to Add an Eatery?
              </AccordionTrigger>
              <AccordionContent>
                No. Each Boost Card is <span className="font-bold">unique to a user</span>. To add an eatery, you must <span className="font-bold">purchase a Boost Card</span> from the eatery, and scan the QR code on the back of the Boost Card in the EatsBoost App
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
